import React, { useMemo } from "react";
import {
  Layers,
  Boxes,
  FileText,
  Clock,
  CalendarDays,
} from "lucide-react";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); SyllabusView.scss deleted with it.
//
// The sheet carried two hues and used each one twice: #3b82f6 for the stat icons
// and the "Day N" badge, #8b5cf6 for the "Module N" badge and the topic bullet.
// Mapped one hue to one token — chart-5 (sky) and chart-1 (violet) — rather than
// to `primary` and `info`, because what they encode is which SHAPE of syllabus
// you are looking at, day-wise or module-wise, and a fixed category colour is
// what the chart ramp is for. They stay distinguishable from each other, and now
// from the brand, in both themes.
//
// Every other colour here was `var(--token, #literal)`, and the fallbacks were a
// second light-mode palette that dark mode never reached. The tokens are enough.
const STAT_ICON = "tw-shrink-0 tw-text-chart-5";
const STAT_VALUE = "tw-text-[1.125rem] tw-font-semibold tw-text-content";
const STAT_LABEL = "tw-text-[0.8125rem] tw-font-medium tw-text-content-muted";

const DAY_ROW =
  "tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-0 tw-border-b " +
  "tw-border-solid tw-border-line tw-px-5 tw-py-3.5 tw-transition-[background] " +
  "tw-duration-100 last:tw-border-b-0 hover:tw-bg-surface-hover " +
  "max-md:tw-flex-col max-md:tw-gap-2.5";

const DAY_BADGE =
  "tw-mt-px tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap " +
  "tw-rounded-[6px] tw-bg-chart-5/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold " +
  "tw-text-chart-5";

// The day row's topics and a topic's subtopics are the same chip.
const CHIP =
  "tw-whitespace-nowrap tw-rounded-[6px] tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface-sunken tw-px-2.5 tw-py-[0.2rem] tw-text-xs tw-font-medium " +
  "tw-text-content-muted";

const MODULE_HEAD =
  "tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-border-0 tw-border-b " +
  "tw-border-solid tw-border-line tw-bg-surface-sunken tw-px-4 tw-py-3";

const MODULE_BADGE =
  "tw-inline-flex tw-items-center tw-gap-[0.3rem] tw-whitespace-nowrap tw-rounded-[6px] " +
  "tw-bg-chart-1/[0.12] tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-chart-1";

const MODULE_META =
  "tw-ms-auto tw-text-xs tw-font-medium tw-text-content-muted max-md:tw-ms-0 max-md:tw-w-full";

const TOPIC_ROW =
  "tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-4 tw-py-3 last:tw-border-b-0";

// ─────────────────────────────────────────────────────────────────────────────
// SyllabusView — read-only renderer for a course syllabus template.
// Handles both formats the template can take:
//   • syllabusType === 'day'    → day-wise list (dayNumber, title, topics[])
//   • syllabusType === 'module' → Module → Topic → Subtopic tree
// Pure presentation: pass the active template object. Theme-aware via tokens.
// ─────────────────────────────────────────────────────────────────────────────

const formatDuration = (mins) => {
  const m = Number(mins) || 0;
  if (m <= 0) return null;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};

const SyllabusView = ({ template }) => {
  const isModule = template?.syllabusType === "module";
  const days = useMemo(() => template?.days || [], [template]);
  const modules = useMemo(() => template?.modules || [], [template]);

  const stats = useMemo(() => {
    if (isModule) {
      let topics = 0;
      let subtopics = 0;
      let duration = 0;
      for (const m of modules) {
        for (const t of m.topics || []) {
          topics += 1;
          duration += Number(t.estimatedDuration) || 0;
          subtopics += (t.subtopics || []).filter((s) => (s || "").trim()).length;
        }
      }
      return { count: modules.length, topics, subtopics, duration };
    }
    const topics = days.reduce(
      (sum, d) => sum + (d.topics || []).filter((t) => (t || "").trim()).length,
      0
    );
    const duration = days.reduce((sum, d) => sum + (Number(d.estimatedDuration) || 0), 0);
    return { count: days.length, topics, duration };
  }, [isModule, days, modules]);

  // Nothing meaningful to render
  if (isModule ? modules.length === 0 : days.length === 0) return null;

  const totalDuration = formatDuration(stats.duration);

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {/* ── Summary stats ── */}
      <div className="tw-flex tw-flex-wrap tw-gap-6 tw-rounded-md tw-bg-surface-sunken tw-px-5 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-2.5">
          {isModule ? <Boxes size={16} className={STAT_ICON} /> : <Layers size={16} className={STAT_ICON} />}
          <div className="tw-flex tw-items-baseline tw-gap-1.5">
            <span className={STAT_VALUE}>{stats.count}</span>
            <span className={STAT_LABEL}>{isModule ? (stats.count === 1 ? "Module" : "Modules") : (stats.count === 1 ? "Day" : "Days")}</span>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <FileText size={16} className={STAT_ICON} />
          <div className="tw-flex tw-items-baseline tw-gap-1.5">
            <span className={STAT_VALUE}>{stats.topics}</span>
            <span className={STAT_LABEL}>{stats.topics === 1 ? "Topic" : "Topics"}</span>
          </div>
        </div>
        {isModule && (
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <Layers size={16} className={STAT_ICON} />
            <div className="tw-flex tw-items-baseline tw-gap-1.5">
              <span className={STAT_VALUE}>{stats.subtopics}</span>
              <span className={STAT_LABEL}>{stats.subtopics === 1 ? "Subtopic" : "Subtopics"}</span>
            </div>
          </div>
        )}
        {totalDuration && (
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <Clock size={16} className={STAT_ICON} />
            <div className="tw-flex tw-items-baseline tw-gap-1.5">
              <span className={STAT_VALUE}>{totalDuration}</span>
              <span className={STAT_LABEL}>Total Duration</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Module-wise ── */}
      {isModule ? (
        <div className="tw-flex tw-flex-col tw-gap-3.5">
          {modules.map((mod) => {
            const topicCount = (mod.topics || []).length;
            const modDuration = formatDuration(
              (mod.topics || []).reduce((s, t) => s + (Number(t.estimatedDuration) || 0), 0)
            );
            return (
              <div
                key={mod.moduleNumber}
                className="tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised"
              >
                <div className={MODULE_HEAD}>
                  <span className={MODULE_BADGE}>
                    <Boxes size={13} /> Module {mod.moduleNumber}
                  </span>
                  <span className="tw-text-[0.9375rem] tw-font-semibold tw-text-content">{mod.title}</span>
                  <span className={MODULE_META}>
                    {topicCount} topic{topicCount === 1 ? "" : "s"}
                    {modDuration ? ` • ${modDuration}` : ""}
                  </span>
                </div>
                <div className="tw-flex tw-flex-col">
                  {(mod.topics || []).map((tp, ti) => (
                    <div key={ti} className={TOPIC_ROW}>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-chart-1" />
                        <span className="tw-text-sm tw-font-semibold tw-text-content">{tp.title}</span>
                        {formatDuration(tp.estimatedDuration) && (
                          <span className="tw-ms-auto tw-inline-flex tw-items-center tw-gap-[0.2rem] tw-whitespace-nowrap tw-text-[0.7rem] tw-font-medium tw-text-content-muted">
                            <Clock size={11} /> {formatDuration(tp.estimatedDuration)}
                          </span>
                        )}
                      </div>
                      {(tp.subtopics || []).filter((s) => (s || "").trim()).length > 0 && (
                        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1.5 tw-ps-3.5">
                          {tp.subtopics
                            .filter((s) => (s || "").trim())
                            .map((sub, si) => (
                              <span key={si} className={CHIP}>{sub}</span>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Day-wise ── */
        <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-line">
          {days.map((day) => (
            <div key={day.dayNumber} className={DAY_ROW}>
              <div className="tw-flex tw-min-w-[200px] tw-shrink-0 tw-items-start tw-gap-3">
                <span className={DAY_BADGE}>
                  <CalendarDays size={12} /> Day {day.dayNumber}
                </span>
                <div className="tw-flex tw-flex-col tw-gap-0.5">
                  <span className="tw-text-sm tw-font-semibold tw-leading-[1.4] tw-text-content">{day.title}</span>
                  <span className="tw-text-xs tw-font-medium tw-text-content-muted">
                    {(day.topics || []).filter((t) => (t || "").trim()).length} topics
                    {formatDuration(day.estimatedDuration) ? ` • ${formatDuration(day.estimatedDuration)}` : ""}
                  </span>
                </div>
              </div>
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-justify-end tw-gap-1.5 max-md:tw-justify-start">
                {(day.topics || [])
                  .filter((t) => (t || "").trim())
                  .map((topic, i) => (
                    <span key={i} className={CHIP}>{topic}</span>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SyllabusView;
